import { cn } from "@/lib/cn";
import { AgentAvatarPreview } from "@/components/ui/agent-avatar-preview";
import { useAuth } from "@/hooks/use-auth";
import type { AgentAvatarStyleKey } from "@/lib/dicebear-styles";
import type { TaskStatus } from "@/api/tasks";
import { useT } from "@/i18n";
import { framesTaskboardMessages } from "@/i18n/messages/frames-taskboard";
import { panelsTasksMessages } from "@/i18n/messages/panels-tasks";
import {
  STATUS_DOT_COLORS,
  TASK_STATUS_LABEL_KEYS,
} from "@/components/shapes/taskboard-frame/task-metadata-chips";

interface SidebarTaskItemProps {
  title: string;
  checked?: boolean;
  status?: string;
  agentLetter?: string;
  agentColor?: string;
  agentSeed?: string;
  onToggle?: () => void;
  onClick?: () => void;
  className?: string;
  "data-testid"?: string;
}

export function SidebarTaskItem({
  title,
  checked = false,
  status,
  agentLetter = "D",
  agentColor: _agentColor,
  agentSeed,
  onToggle,
  onClick,
  className,
  "data-testid": testId,
}: SidebarTaskItemProps) {
  const t = useT(panelsTasksMessages);
  const tBoard = useT(framesTaskboardMessages);
  const { user } = useAuth();
  const avatarStyleKey = (user?.agent_avatar_style ??
    "glass") as AgentAvatarStyleKey;
  const avatarSeed = agentSeed ?? agentLetter;
  const taskStatus = (status ?? "todo") as TaskStatus;
  const taskTestId =
    testId ??
    `sidebar-task-${
      title
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/(^-|-$)/g, "") || "item"
    }`;

  return (
    <div
      className={cn(
        "flex h-appkit-xl w-full items-center gap-appkit-xs px-appkit-md transition-colors hover:bg-glass-stroke-subtle",
        className,
      )}
      data-testid={taskTestId}
    >
      <button
        type="button"
        onClick={onToggle}
        className={cn(
          "flex h-appkit-sm w-appkit-sm shrink-0 items-center justify-center rounded-[calc(var(--appkit-space-2xs)/2)] border",
          checked ? "border-accent bg-accent" : "border-border bg-transparent",
        )}
        aria-label={checked ? t("markIncomplete") : t("markComplete")}
        data-testid={`${taskTestId}-checkbox`}
      >
        {checked && (
          <svg
            className="h-appkit-xs w-appkit-xs text-white"
            viewBox="0 0 8 8"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M1.5 4L3.5 6L6.5 2" />
          </svg>
        )}
      </button>

      <button
        type="button"
        onClick={onClick}
        className={cn(
          "flex flex-1 items-center gap-appkit-2xs truncate text-left text-appkit-caption text-secondary",
          checked && "line-through opacity-60",
        )}
        data-testid={`${taskTestId}-title`}
      >
        <span
          className="h-[calc(var(--appkit-space-2xs)*1.5)] w-[calc(var(--appkit-space-2xs)*1.5)] shrink-0 rounded-pill"
          style={{ backgroundColor: STATUS_DOT_COLORS[taskStatus] }}
          aria-label={tBoard(TASK_STATUS_LABEL_KEYS[taskStatus])}
          data-testid={`${taskTestId}-status`}
        />
        <span className="truncate">{title}</span>
      </button>

      <AgentAvatarPreview
        styleKey={avatarStyleKey}
        seed={avatarSeed}
        sizeClassName="h-appkit-lg w-appkit-lg"
        data-testid={`${taskTestId}-agent`}
      />
    </div>
  );
}

export type { SidebarTaskItemProps };
